// Import number-flow type
import "number-flow";
import type NumberFlow from "number-flow";
import { continuous } from "number-flow";
import { onNextFrame } from "~/solidJs/shared";

class AnimatedNumber {
	private container!: HTMLElement;
	private numberFlowElement!: NumberFlow;
	private currentNumber: number;
	private animationDuration: number;
	private resizeObserver?: ResizeObserver;
	private currentWidth = 0;

	constructor(initialNumber = 0, animationDuration = 1800) {
		this.currentNumber = initialNumber;
		this.animationDuration = animationDuration;
		this.initDOM();
		this.setup(initialNumber);
	}

	private initDOM(): void {
		// Main container with Tailwind classes matching the badge design
		this.container = document.createElement("div");
		this.container.className = "inline-flex items-center gap-0.5";

		// Add smooth width transition to container
		this.container.style.transition = `width ${this.animationDuration}ms ease-out`;
		this.container.style.overflow = "hidden";

		// Create number-flow element
		this.numberFlowElement = document.createElement("number-flow");
		this.numberFlowElement.style.setProperty("--number-flow-mask-height", "0px");
		this.numberFlowElement.style.setProperty("--number-flow-char-height", "12px");
		// Apply styles for consistent appearance and prevent layout shifts
		this.numberFlowElement.style.fontVariantNumeric = "tabular-nums";
		this.numberFlowElement.style.display = "block";

		// Configure formatting to remove comma separators
		this.numberFlowElement.format = {
			style: "decimal",
			useGrouping: false
		};

		this.numberFlowElement.style.minWidth = "fit-content";

		// Configure animation timing to match original behavior
		const duration = this.animationDuration;
		const easing = "ease-out";

		this.numberFlowElement.transformTiming = { duration, easing };
		this.numberFlowElement.spinTiming = { duration, easing };
		this.numberFlowElement.opacityTiming = { duration: 350, easing };
		this.numberFlowElement.plugins = [continuous];

		// Enable animations and respect motion preferences
		this.numberFlowElement.animated = true;
		this.numberFlowElement.respectMotionPreference = false;

		this.container.appendChild(this.numberFlowElement);
	}

	private setup(startNumber: number): void {
		// Set initial value
		this.numberFlowElement.update(startNumber);

		// Setup smooth width animation using ResizeObserver
		this.setupWidthAnimation();

		// Set initial width after a brief delay to allow number-flow to render
		onNextFrame(() => {
			this.currentWidth = this.numberFlowElement.offsetWidth;
			this.container.style.width = `${this.currentWidth}px`;
		});
	}

	private setupWidthAnimation(): void {
		// Use ResizeObserver to detect when number-flow element size changes
		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const newWidth = entry.contentRect.width;
				if (newWidth !== this.currentWidth && newWidth > 0) {
					this.animateWidthChange(newWidth);
					this.currentWidth = newWidth;
				}
			}
		});

		this.resizeObserver.observe(this.numberFlowElement);
	}

	private animateWidthChange(newWidth: number): void {
		// Smoothly animate container width to match number-flow element width
		this.container.style.width = `${newWidth}px`;
	}

	public update(newNumber: number): void {
		if (newNumber === this.currentNumber) return;

		// Use number-flow's built-in update method
		this.numberFlowElement.update(newNumber);
		this.currentNumber = newNumber;
	}

	public getDomElement(): HTMLElement {
		return this.container;
	}

	public getCurrentNumber(): number {
		return this.currentNumber;
	}

	public destroy(): void {
		// Clean up ResizeObserver when component is destroyed
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}
}

export default AnimatedNumber;
